import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from './Button';
import Wizard from './Wizard';
import { ZapIcon, ArrowRightIcon, GlobeIcon, ShareIcon, MessagesSquareIcon, PhoneCallIcon, BotIcon, MailIcon, PlayIcon } from 'lucide-react';
import ConversationNotifier from './ConversationNotifier';
import { useTranslation } from '../i18n/useTranslation';
const HeroAlt = () => {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedAgentType, setSelectedAgentType] = useState('');
  const navigate = useNavigate();
  const {
    t
  } = useTranslation();
  const formRef = useRef<HTMLElement>(null);
  const handleAgentSelection = agentType => {
    setSelectedAgentType(agentType);
    setIsWizardOpen(true);
  };
  const handleLearnMore = () => {
    navigate('/gpt-store');
    window.scrollTo(0, 0);
  };
  const scrollToForm = () => {
    // Try to find a form element on the page to scroll to
    const formElement = document.querySelector('form') || document.querySelector('#contact-form');
    if (formElement) {
      formElement.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      // If no form found, just open the wizard as fallback
      setIsWizardOpen(true);
    }
  };
  return <section className="relative py-24 overflow-hidden bg-white text-gray-800" data-id="element-1752">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1753">
        {/* Main headline */}
        <div className="max-w-4xl mx-auto text-center mb-10" data-id="element-1754">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rhREvAJ4b5296S85upFAnM/logonew.png" alt="Shopilots Logo" className="h-16 mx-auto mb-8" data-id="element-1755" />
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6 text-gray-900" data-id="element-1756">
            {t('hero.title')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-1757">
              {t('hero.subtitle')}
            </span>
          </h1>
          <p className="text-xl text-gray-700 mb-10" data-id="element-1758">{t('hero.description')}</p>
        </div>

        {/* Dynamic Embedding Illustration */}
        <div className="max-w-5xl mx-auto mb-16" data-id="element-1759">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9sv17hTRttNgTQbi53VcZN/ChatGPT_Image_Sep_9%2C_2025%2C_11_31_52_PM.png" alt="Moving from static to dynamic pages with AI-enabled embedding for any channel" className="w-full h-auto rounded-xl shadow-lg border border-gray-200" data-id="element-1760" />
        </div>

        {/* Video Demo Section - Simple, no frame */}
        <div className="max-w-4xl mx-auto mb-16" data-id="element-1761">
          <div className="relative rounded-xl overflow-hidden shadow-2xl bg-black" data-id="element-1762">
            <div className="relative w-full" style={{
            paddingBottom: '56.25%'
          }} data-id="element-1763">
              <iframe className="absolute inset-0 w-full h-full" src="https://app.heygen.com/embedded-player/c820e5db87424fa4a7df4dd131e843c0" title="HeyGen video player" frameBorder="0" allow="encrypted-media; fullscreen;" allowFullScreen data-id="element-1764"></iframe>
            </div>
          </div>
          <div className="text-center mt-6" data-id="element-1765">
            <p className="text-sm text-gray-600 italic" data-id="element-1766">
              "See how our AI shopping assistant helps customers find the
              perfect products, answers questions, and drives sales across all
              platforms."
            </p>
          </div>
        </div>

        {/* Agent selection options */}
        <div className="max-w-5xl mx-auto" data-id="element-1767">
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-800" data-id="element-1768">
            What would you like to build?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-1769">
            <a href="https://get.shopilots.com" target="_blank" rel="noopener noreferrer" className="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-6 text-center transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group" data-id="element-1770">
              <GlobeIcon className="h-10 w-10 mx-auto mb-4 text-indigo-600 group-hover:text-indigo-700 transition-colors" data-id="element-1771" />
              <h3 className="font-semibold mb-2 text-gray-900" data-id="element-1772">
                Website Agent
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1773">
                Embed on your e-commerce site
              </p>
            </a>

            <a href="https://get.shopilots.com" target="_blank" rel="noopener noreferrer" className="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-6 text-center transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group" data-id="element-1774">
              <ShareIcon className="h-10 w-10 mx-auto mb-4 text-indigo-600 group-hover:text-indigo-700 transition-colors" data-id="element-1775" />
              <h3 className="font-semibold mb-2 text-gray-900" data-id="element-1776">
                Social Media Agent
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1777">
                Engage on social platforms
              </p>
            </a>

            <a href="https://get.shopilots.com" target="_blank" rel="noopener noreferrer" className="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-6 text-center transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group" data-id="element-1778">
              <MessagesSquareIcon className="h-10 w-10 mx-auto mb-4 text-indigo-600 group-hover:text-indigo-700 transition-colors" data-id="element-1779" />
              <h3 className="font-semibold mb-2 text-gray-900" data-id="element-1780">
                Messenger Agent
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1781">
                Chat on WhatsApp & Messenger
              </p>
            </a>

            <a href="https://get.shopilots.com" target="_blank" rel="noopener noreferrer" className="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-6 text-center transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group" data-id="element-1782">
              <PhoneCallIcon className="h-10 w-10 mx-auto mb-4 text-indigo-600 group-hover:text-indigo-700 transition-colors" data-id="element-1783" />
              <h3 className="font-semibold mb-2 text-gray-900" data-id="element-1784">Call Agent</h3>
              <p className="text-sm text-gray-600" data-id="element-1785">
                Handle customer phone calls
              </p>
            </a>

            <a href="https://get.shopilots.com" target="_blank" rel="noopener noreferrer" className="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-6 text-center transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group" data-id="element-1786">
              <BotIcon className="h-10 w-10 mx-auto mb-4 text-indigo-600 group-hover:text-indigo-700 transition-colors" data-id="element-1787" />
              <h3 className="font-semibold mb-2 text-gray-900" data-id="element-1788">GPT Store</h3>
              <p className="text-sm text-gray-600" data-id="element-1789">
                Launch in ChatGPT & GPT Store
              </p>
            </a>
          </div>

          <div className="text-center mt-12" data-id="element-1790">
            <a href="mailto:info@shopilots.com" className="inline-flex items-center px-6 py-3 rounded-md bg-purple-600 text-white hover:bg-purple-700 shadow-md hover:shadow-lg transition-all duration-300 font-medium" data-id="element-1791">
              <MailIcon className="mr-2 h-5 w-5" data-id="element-1792" />
              info@shopilots.com
            </a>
            <button onClick={handleLearnMore} className="ml-4 inline-flex items-center px-6 py-3 rounded-md bg-white text-purple-600 border-2 border-purple-600 hover:bg-gray-50 shadow-md hover:shadow-lg transition-all duration-300 font-medium" data-id="element-1793">
              Learn More
              <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1794" />
            </button>
          </div>
        </div>

        {/* Partner logos */}
        <div className="max-w-4xl mx-auto text-center mt-20" data-id="element-1795">
          <p className="text-sm text-gray-600 mb-4" data-id="element-1796">
            Integrate with your existing platforms and AI partners
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12 opacity-80" data-id="element-1797">
            <img src="https://cdn.worldvectorlogo.com/logos/shopify.svg" alt="Shopify" className="h-7" data-id="element-1798" />
            <img src="https://cdn.worldvectorlogo.com/logos/woocommerce.svg" alt="WooCommerce" className="h-7" data-id="element-1799" />
            <img src="https://cdn.worldvectorlogo.com/logos/salesforce-2.svg" alt="Salesforce" className="h-7" data-id="element-1800" />
            <img src="https://cdn.worldvectorlogo.com/logos/openai-2.svg" alt="OpenAI" className="h-7" data-id="element-1801" />
            <img src="https://cdn.worldvectorlogo.com/logos/aws-2.svg" alt="Amazon AWS" className="h-7" data-id="element-1802" />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-4 justify-end" data-id="element-1803">
          <a href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3d8hIh-SBmquhBLzT0K2NNUsyp9W7OJz3kZC5dS4y2t7kSLPqcz7Ll5kRBTrX_hUBi2XAKTHHq" target="_blank" rel="noreferrer" className="px-6 py-3 bg-primary text-white rounded-xl font-bold shadow-lg hover:bg-primary/90 transition-all" data-id="element-1804">
            {t('hero.bookDemo')}
          </a>
          <a href="https://get.shopilots.com" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-gray-100 border border-gray-200 rounded-xl text-slate-700 font-bold hover:bg-gray-200 transition-all" data-id="element-1805">
            {t('hero.createAssistant')}
          </a>
        </div>
      </div>

      {/* Add the conversation notifier component */}
      <ConversationNotifier data-id="element-1806" />

      <Wizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} initialAgentType={selectedAgentType} data-id="element-1807" />
    </section>;
};
export default HeroAlt;